import { yupResolver } from '@hookform/resolvers/yup';
import { AxiosError } from 'axios';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ErrorInfo, LegalRegion, PostSectionsPsdiffcheckBody } from '../../../../../api/model';
import { usePostSectionsPsdiffcheck } from '../../../../../api/sections/sections';
import { usePostUtilsDownload } from '../../../../../api/utils/utils';
import { useDefaultOptions, useOptionsForBinary } from '../../../../../hooks/api';
import '../../../../../styles/Dropdown.css';
import yup from '../../../../../utils/custom-yup';
import { executeDownload } from '../../../../../utils/download';
import MultiSelect from '../../../../atoms/MultiSelect';
import BlobErrorModal from '../../../../modules/BlobErrorModal';
import ConfirmModal from '../../../../modules/ConfirmModal';
import ErrorModal from '../../../../modules/ErrorModal';
import Modal from '../../../../modules/Modal';
import SpinnerModal from '../../../../modules/SpinnerModal';

type Props = {
  open: boolean;
  onClose: () => void;
  sectionId: number;
  legalRegionData: LegalRegion[];
};
function PsDiffLModal({ open, onClose, sectionId, legalRegionData }: Props) {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const legalRegionMessage = t('法規適用国を選択してください。');
  const schema = yup.object().shape({
    legal_region_ids: yup.array().min(1, legalRegionMessage),
  });

  // フォーム要素
  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<PostSectionsPsdiffcheckBody>({
    defaultValues: {
      diff_type: 2,
      output_type: '1',
      language_id: '0',
      legal_region_ids: [],
    },
    resolver: yupResolver(schema),
  });

  const [displaySpinnerModal, setDisplaySpinnerModal] = useState(false); // スピナーモーダルを表示する
  const [displayPsDiffError, setDisplayPsDiffError] = useState(false); // エラー
  const [displayConfirmPsDiffRun, setDisplayConfirmPsDiffRun] = useState(false); // 確認
  const [displayDownLoadError, setDisplayDownLoadError] = useState(false); // ダウンロードエラー

  const options = useDefaultOptions(language);
  const optionsForBinary = useOptionsForBinary(language);

  // PS差異API
  const {
    mutateAsync: _pdDiffAsync,
    isError: psDiffIsError,
    error: psDiffError,
  } = usePostSectionsPsdiffcheck<AxiosError<ErrorInfo>, unknown>(options);

  useEffect(() => {
    setDisplayPsDiffError(psDiffIsError);
  }, [psDiffIsError]);

  // ダウンロードAPI
  const {
    mutateAsync: _downloadAsync,
    isError: downloadIsError,
    error: downloadError,
  } = usePostUtilsDownload<AxiosError<Blob>>(optionsForBinary);
  useEffect(() => {
    setDisplayDownLoadError(downloadIsError);
  }, [downloadIsError]);

  // PS差異処理
  const onSubmit = useCallback(
    () =>
      handleSubmit(async (data: PostSectionsPsdiffcheckBody) => {
        setDisplaySpinnerModal(true);
        if (sectionId != null) {
          const responseDiff = await _pdDiffAsync({
            sectionId,
            data,
          });
          if (responseDiff.status === 200) {
            // 処理が成功したら結果をダウンロード
            if (responseDiff.data.result_file_info !== undefined && responseDiff.data.result_file_info !== null) {
              await _downloadAsync(
                { data: responseDiff.data.result_file_info },
                {
                  onSuccess: (responseDL) => {
                    executeDownload(responseDL);
                  },
                }
              );
            }
            // ダイアログを閉じる
            void onClose();
          }
        }
        setDisplaySpinnerModal(false);
      })(),
    [sectionId, _pdDiffAsync, _downloadAsync, onClose, handleSubmit]
  );

  return (
    <>
      <Modal active={open} additionalClassName="popupfr600" displayCloseButton onClose={onClose}>
        <form>
          <div className="popupfr600 popuppsdiff">
            <div className="title">
              <label>{t('法規確認')}</label>
            </div>
            <div className="selecter">
              <div className="labip3">
                <div className="mb5 mt5">
                  <label>{t('言語')}</label>
                </div>
                <div>
                  <input className="radionr" type="radio" value={0} {...register('language_id')} id="lang_ja" />
                  <label htmlFor="lang_ja">{t('日本語')}</label>
                </div>
                <div>
                  <input className="radionr" type="radio" value={1} {...register('language_id')} id="lang_us" />
                  <label htmlFor="lang_us">{t('米語')}</label>
                </div>
                <div>
                  <input className="radionr" type="radio" value={2} {...register('language_id')} id="lang_en" />
                  <label htmlFor="lang_en">{t('英語')}</label>
                </div>
              </div>
              <div className="labip7">
                <div>
                  <label htmlFor="legal_region_ids" style={{ width: '100px' }}>
                    {t('法規適用国')}
                  </label>
                  <MultiSelect
                    id="legal_region_ids"
                    name="legal_region_ids"
                    options={legalRegionData}
                    control={control}
                    isDisabled={false}
                    getValue={(x) => x.legal_region_id}
                    getLabel={(x) => x.legal_region_name}
                  />
                  {errors.legal_region_ids?.message && (
                    <p className="errormessage show">{errors.legal_region_ids?.message}</p>
                  )}
                </div>
              </div>
            </div>
            <div className="btndiv">
              <div>
                <span className="btn2 btnbg-g mr10">
                  <span className="modal-close2" onClick={onClose}>
                    {t('キャンセル').toString()}
                  </span>
                </span>
                <span>
                  <input
                    type="button"
                    value={t('出力').toString()}
                    className="btn1 lsp1"
                    onClick={() => setDisplayConfirmPsDiffRun(true)}
                  />
                </span>
              </div>
            </div>
          </div>
        </form>
      </Modal>
      {/* スピナーモーダル */}
      {displaySpinnerModal && <SpinnerModal />}
      {/* エラー */}
      {displayPsDiffError && (
        <ErrorModal
          error={psDiffError}
          onClickOk={() => {
            setDisplayPsDiffError(false);
            setDisplaySpinnerModal(false);
          }}
        />
      )}
      {/* 確認 */}
      {displayConfirmPsDiffRun && (
        <ConfirmModal
          onClickCancel={() => setDisplayConfirmPsDiffRun(false)}
          onClickOk={async () => {
            setDisplayConfirmPsDiffRun(false);
            await onSubmit();
          }}
        >
          {t('法規チェックを実行しますか？')}
        </ConfirmModal>
      )}
      {/* エラー */}
      {displayDownLoadError && (
        <BlobErrorModal error={downloadError} onClickOk={() => setDisplayDownLoadError(false)} />
      )}
    </>
  );
}

export { PsDiffLModal };
